import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Grid,
  Modal,
  Typography,
} from "@mui/material";
import { ReactNode } from "react";
import { FormModalStyle } from "#next/components/modals/styles";

interface IFormModalControls {
  onClose: () => void;
  onSave: () => void;
  saveIsDisabled: boolean;
  saveButtonName: string;
  closeButtonName?: string;
}

interface IFormModalControlsNoSave
  extends Omit<
    IFormModalControls,
    "onSave" | "saveIsDisabled" | "saveButtonName"
  > {
  onSave?: undefined;
}

export function FormModalControls({
  onClose,
}: IFormModalControlsNoSave): JSX.Element;
export function FormModalControls({
  onClose,
  onSave,
  saveIsDisabled,
  saveButtonName,
}: IFormModalControls): JSX.Element;
export function FormModalControls({
  onClose,
  closeButtonName,
  ...props
}: IFormModalControls | IFormModalControlsNoSave): JSX.Element {
  return (
    <>
      <Divider sx={{ mt: 6, mb: 2 }} />
      <Grid container>
        <Grid item flexGrow="1">
          <Button variant="contained" onClick={onClose}>
            {closeButtonName ?? "Cancel"}
          </Button>
        </Grid>
        {props.onSave && (
          <Grid item>
            <Button
              variant="contained"
              color="success"
              disabled={props.saveIsDisabled}
              onClick={props.onSave}
            >
              {props.saveButtonName}
            </Button>
          </Grid>
        )}
      </Grid>
    </>
  );
}

export const FormModalTitle = ({ title }: { title: string }) => (
  <Typography variant="h2" sx={{ mb: 1 }}>
    {title}
  </Typography>
);

export interface IFormModal {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
}

export const FormModal = ({ open, onClose, children }: IFormModal) => {
  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={FormModalStyle}>{children}</Box>
    </Modal>
  );
};

interface IFormModalLoading extends Omit<IFormModal, "children"> {
  title: string;
}

export const FormModalLoading = ({
  open,
  onClose,
  title,
}: IFormModalLoading) => {
  return (
    <FormModal open={open} onClose={onClose}>
      <Grid container direction="column">
        <FormModalTitle title={title} />
        <Grid item>
          <CircularProgress />
        </Grid>
      </Grid>
      <FormModalControls onClose={onClose}></FormModalControls>
    </FormModal>
  );
};

interface IFormModalWithMessage extends IFormModalLoading {
  message: string;
}

export const FormModalWithMessage = ({
  open,
  onClose,
  title,
  message,
}: IFormModalWithMessage) => {
  return (
    <FormModal open={open} onClose={onClose}>
      <Grid container direction="column">
        <FormModalTitle title={title} />
        <Grid item>
          <Typography>{message}</Typography>
        </Grid>
      </Grid>
      <FormModalControls
        onClose={onClose}
        closeButtonName="Close"
      ></FormModalControls>
    </FormModal>
  );
};

export const FormModalWithErrorMessage = ({
  ...props
}: Omit<IFormModalWithMessage, "message">) => {
  return <FormModalWithMessage {...props} message="Something went wrong" />;
};
